import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Logo } from "@/components/layout/Logo";

const Frame = ({ children }) => (
  <div className="noise flex min-h-screen flex-col bg-[#050505] text-white">
    <div className="border-b border-white/10 px-6 py-4"><Logo /></div>
    <div className="flex flex-1 items-center px-6"><div className="mx-auto w-full max-w-xl">{children}</div></div>
  </div>
);

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", agentId: null });

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState({ status: "paid", agentId: data.agent_id, productId: data.product_id });
        if (["expired", "failed"].includes(data.payment_status)) return setState({ status: "failed", agentId: data.agent_id });
      } catch { /* retry */ }
      attempts += 1;
      if (attempts >= 15) return setState((s) => ({ ...s, status: "timeout" }));
      timer = setTimeout(poll, 2000);
    };
    if (sessionId) poll(); else setState({ status: "failed" });
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Frame>
      <div className="panel p-10" data-testid="payment-status-panel">
        {state.status === "checking" && (<><CircleNotch size={36} className="animate-spin text-blue-500" /><h1 className="mt-6 font-display text-3xl font-bold">Confirmando el pago…</h1><p className="mt-2 text-zinc-400">Esto solo tarda unos segundos.</p></>)}
        {state.status === "paid" && (
          <>
            <CheckCircle size={40} weight="fill" className="text-emerald-500" />
            <h1 className="mt-6 font-display text-3xl font-bold" data-testid="payment-success-title">¡Pago completado!</h1>
            <p className="mt-2 text-zinc-400">{state.productId ? "Tu producto y su licencia ya están en tu biblioteca." : "El agente ya está en tu biblioteca y listo para trabajar."}</p>
            <div className="mt-8 flex gap-3">
              {state.productId ? (
                <Link to={`/product/${state.productId}`} className="btn-primary" data-testid="payment-open-product">Descargar ahora</Link>
              ) : (
                <Link to={`/chat/${state.agentId}`} className="btn-primary" data-testid="payment-open-chat">Empezar a usarlo</Link>
              )}
              <Link to="/library" className="btn-ghost" data-testid="payment-library-link">Ver biblioteca</Link>
            </div>
          </>
        )}
        {["failed", "timeout"].includes(state.status) && (
          <>
            <XCircle size={40} weight="fill" className="text-red-500" />
            <h1 className="mt-6 font-display text-3xl font-bold">{state.status === "timeout" ? "Seguimos verificando" : "El pago no se completó"}</h1>
            <p className="mt-2 text-zinc-400">{state.status === "timeout" ? "Tu pago puede tardar un poco más. Revisa tu biblioteca en unos minutos." : "No se ha realizado ningún cargo."}</p>
            <div className="mt-8 flex gap-3"><Link to="/library" className="btn-primary">Ir a biblioteca</Link><Link to="/marketplace" className="btn-ghost">Marketplace</Link></div>
          </>
        )}
      </div>
    </Frame>
  );
};

export const PaymentCancel = () => {
  const [params] = useSearchParams();
  const agentId = params.get("agent_id");
  const productId = params.get("product_id");
  return (
    <Frame>
      <div className="panel p-10" data-testid="payment-cancel-panel">
        <XCircle size={40} className="text-zinc-500" />
        <h1 className="mt-6 font-display text-3xl font-bold">Pago cancelado</h1>
        <p className="mt-2 text-zinc-400">No se ha realizado ningún cargo. Puedes volver a intentarlo cuando quieras.</p>
        <div className="mt-8 flex gap-3">
          {agentId && <Link to={`/agent/${agentId}`} className="btn-primary" data-testid="payment-cancel-back">Volver al agente</Link>}
          {productId && <Link to={`/product/${productId}`} className="btn-primary" data-testid="payment-cancel-back-product">Volver al producto</Link>}
          <Link to="/marketplace" className="btn-ghost">Marketplace</Link>
        </div>
      </div>
    </Frame>
  );
};
